import React from 'react';
import { scaleTime, scaleLinear } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { Group } from '@visx/group';
import { Grid } from '@visx/grid';
import { localPoint } from '@visx/event';
import { useTheme } from '@mui/material';
import { curveStepAfter } from '@visx/curve';
import { useTooltip } from '@visx/tooltip';
import { culture } from '../../Helpers/dates';
import Scatter from './Scatter';
import TooltipWrapper from './TooltipWrapper';
import TimelineStepper from './TimelineStepper';
import { useIntl } from 'react-intl';

const margin = { top: 40, right: 40, bottom: 40, left: 60 };

export default function TimelineChart({
  events,
  width,
  height,
  period,
  startDate,
  endDate,
  onChangePeriod,
  onClick,
}) {
  const intl = useIntl();
  const theme = useTheme();
  const xMin = 0;
  const yMin = 0;
  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  // Normalize to midnight to ignore time component
  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Normalize all event dates
  const normalizedEvents = events.map((event) => ({
    ...event,
    date: normalizeDate(event.date),
  }));

  const first = startDate ? normalizeDate(startDate).valueOf() : null;
  const last = endDate ? normalizeDate(endDate).valueOf() : null;

  const {
    tooltipOpen,
    tooltipTop,
    tooltipLeft,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip();

  const xScale = scaleTime({
    domain: [new Date(first), new Date(last)],
    nice: false,
  });
  const yScale = scaleLinear({
    domain: [0, 100],
    nice: true,
  });

  const handleMouseOver = (event, d) => {
    const coords = localPoint(event);
    showTooltip({
      tooltipLeft: coords.x - 115,
      tooltipTop: coords.y - 100, // Offset tooltip to appear above the point
      tooltipData: d,
    });
  };

  xScale.range([xMin, xMax]);
  yScale.range([yMax, yMin]);

  // Generate daily ticks for each day in the range
  const dailyTicks = [];
  const currentDate = new Date(first);
  while (currentDate <= new Date(last)) {
    dailyTicks.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return (
    <div style={{ position: 'relative' }}>
      <TooltipWrapper
        open={tooltipOpen}
        data={tooltipData}
        tooltipPosition={{ top: tooltipTop, left: tooltipLeft }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
          }}
        >
          <strong>{tooltipData?.name}</strong>
          <div>
            {intl.messages[tooltipData?.status] || tooltipData?.status} ·{' '}
            {tooltipData?.date.toLocaleDateString(culture, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </div>
        </div>
      </TooltipWrapper>
      <svg width={width} height={height}>
        <defs>
          {/** Clip path for chart area and border radius */}
          <clipPath id="chart-area">
            <rect
              x={margin.left}
              y={margin.top}
              width={xMax}
              height={yMax}
              rx={10}
              ry={10}
            />
          </clipPath>
        </defs>
        <rect
          x={margin.left}
          y={margin.top}
          width={xMax}
          height={yMax}
          rx={10}
          ry={10}
          fill="none"
          stroke={theme.palette.greys.lighter}
          strokeWidth={1}
        />
        <g clipPath="url(#chart-area)">
          <Grid
            top={margin.top}
            left={margin.left}
            xScale={xScale}
            yScale={yScale}
            stroke={theme.palette.greys.lighter}
            numTicksRows={5}
            columnTickValues={dailyTicks}
            width={xMax}
            height={yMax}
          />
        </g>
        <AxisBottom
          top={yMax + margin.top}
          left={margin.left}
          hideTicks
          hideAxisLine
          scale={xScale}
          stroke={theme.palette.greys.lighter}
          tickValues={dailyTicks}
          tickFormat={(date) =>
            date.toLocaleDateString(culture, {
              day: '2-digit',
              month: '2-digit',
            })
          }
          tickLabelProps={() => ({
            fill: theme.palette.greys.lighter,
            textAnchor: 'middle',
            fontSize: 14,
            fontFamily: 'Roboto',
          })}
        />
        <AxisLeft
          hideTicks
          hideAxisLine
          numTicks={5}
          top={margin.top}
          left={margin.left}
          scale={yScale}
          stroke={theme.palette.greys.lighter}
          tickValues={yScale?.ticks(5)}
          tickFormat={(value) => `${value}%`}
          tickLabelProps={() => ({
            fill: theme.palette.greys.lighter,
            textAnchor: 'end',
            dx: -10,
            fontSize: 14,
            fontFamily: 'Roboto',
          })}
        />
        <Group top={margin.top} left={margin.left}>
          <LinePath
            data={normalizedEvents}
            x={(d) => xScale(d.date)}
            y={(d) => yScale(d.value)}
            stroke="#1E88E5"
            strokeWidth={5}
            curve={curveStepAfter} //curveStepAfter for step line instead of straight line
          />
          {normalizedEvents.map((d, i) => (
            <Scatter
              key={i}
              cx={xScale(d.date)}
              cy={yScale(d.value)}
              onMouseOver={(e) => handleMouseOver(e, d)}
              onMouseLeave={hideTooltip}
              icon={d.icon}
              color={d.color}
              size={15}
              onClick={() => (d?.id ? onClick(d?.id) : {})}
            />
          ))}
        </Group>
      </svg>
      {period && (
        <TimelineStepper period={period} onChangePeriod={onChangePeriod} />
      )}
    </div>
  );
}
